import { useEffect } from "react";
import { useDrag, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import type { SmallTeamId } from "../../../api/smallTeam";
import type { UserId } from "../../../api/user";
import type { WorkshopParticipant } from "../../../api/workshop";
import { ItemType } from "../../../design-system/components/UserDisplay/UserDisplay.jsx";
import { isMobileDevice } from "../../../lib/device-utils.js";
import { isSafariBrowser } from "../../../lib/is-safari.js";

export type Group = { id: SmallTeamId; name: string };

/** The sentinel for "not in any small team" — `smallTeamId` on a student, and `targetGroupId` on a drop zone, are both this rather than `undefined`. */
export const NO_SMALL_TEAM_ID = null;

export type MoveStudent = (
  studentId: UserId,
  groupId: SmallTeamId | null,
) => void;

type DraggedStudent = {
  id: UserId;
  groupId: SmallTeamId | null;
  userNameAndTag: string;
};

/**
 * Wires a student up as a react-dnd drag source (the shared `ItemType.USER`),
 * and hides the native browser drag preview on Safari/mobile, where it
 * causes a ghost-image glitch — the board renders its own drag layer instead.
 */
export const useStudentDragSource = (student: WorkshopParticipant) => {
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: ItemType.USER,
      item: {
        id: student.id,
        groupId: student.smallTeamId,
        userNameAndTag: `${student.name}#${student.tag}`,
      } satisfies DraggedStudent,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [student.id, student.smallTeamId],
  );

  useEffect(() => {
    if (isMobileDevice() || isSafariBrowser()) {
      preview(getEmptyImage(), { captureDraggingState: true });
    }
  }, [preview]);

  return { drag, isDragging };
};

type UseStudentDropOptions = {
  targetGroupId: SmallTeamId | null;
  moveStudent: MoveStudent;
};

/**
 * Makes an element a drop target for a dragged student (paired with
 * `useStudentDragSource`) — moves them into `targetGroupId` unless they're
 * already there.
 */
export const useStudentDrop = ({
  targetGroupId,
  moveStudent,
}: UseStudentDropOptions) => {
  const [{ isOver }, drop] = useDrop<DraggedStudent, void, { isOver: boolean }>(
    {
      accept: ItemType.USER,
      drop: (item) => {
        if (item.groupId !== targetGroupId) {
          moveStudent(item.id, targetGroupId);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    },
  );

  return { isOver, drop };
};
